/**
 * PSD2 bank-consent OAuth bridge (pfh-mobile#31 — IOS-3.5).
 *
 * On iOS, PSD2 / Enable Banking consent redirects MUST NOT happen inside the
 * WKWebView because the app WebView and Safari have separate cookie jars, which
 * breaks some bank IdP flows that expect a fresh system browser session.
 *
 * openConsentUrl() uses @capacitor/browser which maps to:
 *   - iOS: SFSafariViewController (same engine as ASWebAuthenticationSession;
 *          shares the system cookie jar and sandbox; App Store approved).
 *   - Android: Chrome Custom Tabs.
 *   - Web/PWA: window.open (no-op wrapper — the regular browser already has the
 *              system cookie jar).
 *
 * handleConsentCallback() must be called once at app startup. It listens for
 * deep-link callbacks delivered via App.addListener('appUrlOpen') and resolves
 * the pending consent promise so the caller can read the authorization code /
 * error from the redirect URL.
 *
 * URL scheme: the Enable Banking backend issues a redirect_uri pointing to
 * `app.foroba.pfh://consent/callback` (matches the iOS CFBundleURLSchemes
 * entry in Info.plist). The native OS hands this URL to the app after the
 * browser session closes.
 */

import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { App } from '@capacitor/app'

// ── Pending consent state ─────────────────────────────────────────────────────

type ConsentResult = { callbackUrl: string }
type ConsentResolver = (result: ConsentResult) => void
type ConsentRejecter = (reason: Error) => void

let _pendingResolve: ConsentResolver | null = null
let _pendingReject: ConsentRejecter | null = null

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Open a PSD2 consent URL in a secure browser session.
 *
 * On native platforms (iOS/Android) this uses the system browser so the user's
 * existing bank session is available.  On web the URL is opened in the current
 * tab (standard redirect flow).
 *
 * Returns a promise that resolves with the callback URL once the OS delivers
 * the deep-link back to the app, or rejects if the browser session is closed
 * without a callback (user cancelled).
 *
 * @example
 * const { callbackUrl } = await openConsentUrl(consentUrl)
 * const params = new URL(callbackUrl).searchParams
 * const code = params.get('code')
 */
export async function openConsentUrl(consentUrl: string): Promise<ConsentResult> {
  if (!Capacitor.isNativePlatform()) {
    // Web/PWA: standard full-page redirect; no promise resolution needed.
    window.location.href = consentUrl
    // Return a never-resolving promise — the page will reload after redirect.
    return new Promise(() => {})
  }

  // Cancel any previously pending consent flow.
  if (_pendingReject) {
    _pendingReject(new Error('New consent flow started'))
    _pendingReject = null
    _pendingResolve = null
  }

  const result = await new Promise<ConsentResult>((resolve, reject) => {
    _pendingResolve = resolve
    _pendingReject = reject
  })

  await Browser.open({ url: consentUrl, presentationStyle: 'popover' })

  return result
}

/**
 * Close the in-app browser after a successful consent redirect.
 * Call this after reading the callback URL from openConsentUrl().
 */
export async function closeConsentBrowser(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await Browser.close()
  }
}

// ── Callback handler (call once at startup) ───────────────────────────────────

let _callbackHandlerRegistered = false

/**
 * Register the deep-link listener that resolves pending consent flows.
 *
 * Must be called once at app startup (before any openConsentUrl call).
 * Safe to call multiple times — registers at most once.
 *
 * The app URL scheme `app.foroba.pfh://consent/callback?...` is delivered
 * here after the bank redirects back to the app.
 */
export function registerConsentCallbackHandler(): void {
  if (!Capacitor.isNativePlatform() || _callbackHandlerRegistered) return
  _callbackHandlerRegistered = true

  void App.addListener('appUrlOpen', ({ url }) => {
    if (!url.includes('/consent/callback')) return

    if (_pendingResolve) {
      _pendingResolve({ callbackUrl: url })
      _pendingResolve = null
      _pendingReject = null
    }

    // Close the in-app browser (SFSafariViewController) after callback.
    void Browser.close()
  })
}
